import Papa from 'papaparse';

import { CsvBulkConfig, CsvFieldDefinition, CsvParsedRow } from './types';

/**
 * CSVテンプレート文字列を生成する
 */
export const generateCsvTemplate = (fields: CsvFieldDefinition[]): string => {
  const headers = fields.map((f) => f.header);
  return Papa.unparse([headers], { header: false });
};

/**
 * CSVテンプレートをダウンロードする
 */
export const downloadCsvTemplate = (fields: CsvFieldDefinition[], fileName: string): void => {
  const csv = generateCsvTemplate(fields);
  const bom = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * CSVファイルをパースしてオブジェクト配列に変換する
 */
export const parseCsvFile = (file: File): Promise<Record<string, string>[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSVパースエラー: ${results.errors[0].message}`));
          return;
        }
        resolve(results.data);
      },
      error: (error) => {
        reject(new Error(`CSVファイルの読み込みに失敗しました: ${error.message}`));
      },
    });
  });
};

/**
 * パースしたCSVデータをバリデーションしてCsvParsedRow配列に変換する
 */
export const validateCsvData = <TParams>(
  data: Record<string, string>[],
  config: CsvBulkConfig<TParams>,
): CsvParsedRow[] => {
  return data.map((rawRow, index) => {
    const row: Record<string, string> = {};
    const errorFields: string[] = [];
    const errorMessages: string[] = [];

    // ヘッダー名からキーへのマッピング
    config.fields.forEach((field) => {
      const rawValue = rawRow[field.header] ?? '';
      row[field.key] = rawValue.trim();
    });

    // バリデーション
    config.fields.forEach((field) => {
      const value = row[field.key] ?? '';

      // 必須チェック
      if (field.required && !value) {
        errorFields.push(field.key);
        errorMessages.push(`${field.header}は必須です`);
        return;
      }

      // 値がない場合はスキップ
      if (!value) return;

      // maxLengthチェック
      if (field.maxLength && value.length > field.maxLength) {
        errorFields.push(field.key);
        errorMessages.push(`${field.header}は${field.maxLength}文字以内で入力してください`);
        return;
      }

      // select/radio: コード値の存在チェック
      if ((field.type === 'select' || field.type === 'radio') && field.options) {
        const validCodes = field.options.map((o) => o.code);
        if (!validCodes.includes(value)) {
          errorFields.push(field.key);
          errorMessages.push(`${field.header}のコード値「${value}」は無効です`);
          return;
        }
      }

      // multi-select: 各値のコード存在チェック
      if (field.type === 'multi-select' && field.options) {
        const separator = field.separator || ',';
        const values = value.split(separator).map((v) => v.trim());
        const validCodes = field.options.map((o) => o.code);
        const invalidValues = values.filter((v) => v && !validCodes.includes(v));
        if (invalidValues.length > 0) {
          errorFields.push(field.key);
          errorMessages.push(`${field.header}のコード値「${invalidValues.join(', ')}」は無効です`);
          return;
        }
      }

      // check: boolean値チェック
      if (field.type === 'check') {
        const validValues = ['true', 'false', '1', '0', 'TRUE', 'FALSE'];
        if (!validValues.includes(value)) {
          errorFields.push(field.key);
          errorMessages.push(`${field.header}は true/false で入力してください`);
          return;
        }
      }

      // カスタムバリデーション
      if (config.validateField) {
        const customError = config.validateField(field.key, value, row);
        if (customError) {
          errorFields.push(field.key);
          errorMessages.push(customError);
        }
      }
    });

    return {
      index,
      data: row,
      status: errorFields.length === 0 ? 'success' : 'error',
      errorFields,
      errorMessages,
    };
  });
};

/**
 * コード値を表示値に変換する（フィールド定義のoptionsから解決）
 */
export const resolveDisplayValueFromOptions = (
  fields: CsvFieldDefinition[],
  key: string,
  rawValue: string,
): string | null => {
  const field = fields.find((f) => f.key === key);
  if (!field || !field.options) return null;

  if (field.type === 'multi-select') {
    const separator = field.separator || ',';
    const values = rawValue.split(separator).map((v) => v.trim());
    const labels = values
      .map((v) => {
        const option = field.options?.find((o) => o.code === v);
        return option ? option.label : null;
      })
      .filter(Boolean);
    return labels.length > 0 ? labels.join(', ') : null;
  }

  if (field.type === 'check') {
    if (rawValue === 'true' || rawValue === '1' || rawValue === 'TRUE') return 'はい';
    if (rawValue === 'false' || rawValue === '0' || rawValue === 'FALSE') return 'いいえ';
    return null;
  }

  const option = field.options.find((o) => o.code === rawValue);
  return option ? option.label : null;
};
